import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, AlertCircle } from "lucide-react";
import { bootstrapAuth } from "../lib/api";
import { useAuth } from "../lib/auth-context";

const ERROR_COPY = {
  state_mismatch: {
    title: "Sign-in expired",
    body: "Your sign-in request expired or didn't match. Please try signing in again.",
    cta: "Back to sign in",
  },
  oauth_denied: {
    title: "Sign-in cancelled",
    body: "Google sign-in was cancelled.",
    cta: "Back to sign in",
  },
  email_not_verified: {
    title: "Verify your Google email",
    body: "Your Google account email isn't verified yet. Please verify it in your Google account settings, then try again.",
    cta: "Back to sign in",
  },
  redirect_uri_mismatch: {
    title: "Configuration error",
    body: "Sign-in is misconfigured for this deployment. Please contact support. (ref: redirect_uri_mismatch)",
    cta: "Back to sign in",
  },
  signin_not_configured: {
    title: "Sign-in unavailable",
    body: "Google sign-in is temporarily unavailable on this deployment.",
    cta: "Back to sign in",
  },
  internal: {
    title: "Sign-in temporarily unavailable",
    body: "Something went wrong. Please try again in a moment.",
    cta: "Try again",
  },
};

export default function AuthCallback() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const hasProcessed = useRef(false);
  const [errorState, setErrorState] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const run = async () => {
      const search = new URLSearchParams(window.location.search);
      const errCode = search.get("error");
      const next = search.get("next") || "/dashboard";

      if (errCode) {
        setErrorState(ERROR_COPY[errCode] || ERROR_COPY.internal);
        // Strip the URL so reloads don't replay
        window.history.replaceState(null, "", "/auth/callback");
        return;
      }

      // Phase 5: the backend OAuth callback already set the httpOnly refresh
      // cookie (no token in the URL anymore — kills the history/Referer leak).
      // Bootstrap an access token from that cookie, then load the profile.
      const ok = await bootstrapAuth();
      if (!ok) {
        navigate("/login?reason=refresh_failed", { replace: true });
        return;
      }

      try {
        const me = await refresh();
        if (me?.member) {
          navigate(next.startsWith("/") ? next : "/dashboard", { replace: true });
        } else if (me) {
          navigate("/onboarding", { replace: true });
        } else {
          navigate("/login?reason=refresh_failed", { replace: true });
        }
      } catch (e) {
        // Access token works (just minted) but /auth/me failed — treat as
        // transient; the dashboard's global 401 handler will recover.
        navigate("/dashboard", { replace: true });
      }
    };
    run();
  }, [navigate, refresh]);

  if (errorState) {
    return (
      <div
        className="flex h-screen w-full items-center justify-center bg-ink-50 px-4"
        data-testid="auth-callback-error"
      >
        <div className="max-w-md w-full hp-card p-6 space-y-3">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-amber-700 mt-0.5" strokeWidth={1.5} />
            <div>
              <h1 className="font-display text-lg font-medium text-ink-900">{errorState.title}</h1>
              <p className="mt-1 text-[13px] text-ink-600">{errorState.body}</p>
              {errorState.debugCategory && (
                <p
                  className="mt-2 font-mono text-[11px] text-ink-400"
                  data-testid="auth-callback-error-debug"
                >
                  ref: {errorState.debugCategory}
                </p>
              )}
            </div>
          </div>
          <button
            className="hp-btn-primary w-full"
            onClick={() => navigate("/login", { replace: true })}
            data-testid="auth-callback-error-cta"
          >
            {errorState.cta}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex h-screen w-full items-center justify-center bg-ink-50"
      data-testid="auth-callback-page"
    >
      <div className="flex flex-col items-center gap-3 text-ink-500">
        <Loader2 className="h-5 w-5 animate-spin" strokeWidth={1.5} />
        <span className="text-sm">Finishing sign in…</span>
      </div>
    </div>
  );
}
